/**
 * A escolha sobre cookies de medição.
 *
 * Guardada no `localStorage`, que é uma das poucas coisas que se pode guardar
 * **sem** consentimento: é estritamente necessária para cumprir a escolha de
 * quem a fez. Não guardar nada obrigaria a perguntar outra vez a cada página,
 * o que é pior para quem visita e não é mais privado.
 *
 * **`window.localStorage` e não `localStorage` à seca.** O Node 24 traz um
 * `localStorage` próprio, experimental, que num ambiente de teste sombreia o
 * do browser e não implementa a interface toda. Escrever `window.` amarra
 * isto ao armazenamento da página, que é o único que faz sentido para uma
 * escolha de quem visita.
 *
 * O que importa aqui é o `null`. Três estados e não dois: sim, não, e **ainda
 * não respondeu** — e enquanto for `null` nada de medição arranca. Um booleano
 * colapsava "recusou" e "ainda não decidiu" no mesmo valor, e a diferença
 * entre os dois é a diferença entre cumprir a lei e não cumprir.
 */

export type Consent = 'granted' | 'denied';

const KEY = 'et-analytics-consent';

/** `null` = ainda não respondeu. */
export function readConsent(): Consent | null {
  try {
    const v = window.localStorage.getItem(KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    // Navegação privada, cookies bloqueados, storage cheio. Sem sítio onde
    // guardar a escolha, o estado seguro é o que não mede nada.
    return null;
  }
}

export function writeConsent(value: Consent): void {
  try {
    window.localStorage.setItem(KEY, value);
  } catch {
    // Não poder guardar não pode impedir a escolha de valer nesta sessão:
    // quem chama isto actualiza o seu próprio estado à mesma.
  }
}
